import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { TrendingUp, Calculator, ArrowLeft } from "lucide-react";
import { Button } from "@/common/components/ui/button";
import { Card, CardContent } from "@/common/components/ui/card";
import { CompactCalculatorRow } from "../CompactCalculatorRow";
import { calculateRD, formatCurrency } from "../../utils/calculations";
import ProductLayout from "@/common/components/ProductLayout";

export const RDCalculator = () => {
  const navigate = useNavigate();
  const [rdAmount, setRdAmount] = useState(5000);
  const [rdRate, setRdRate] = useState(6.5);
  const [rdYears, setRdYears] = useState(5);

  const rdResult = useMemo(
    () => calculateRD(rdAmount, rdRate, rdYears),
    [rdAmount, rdRate, rdYears]
  );

  return (
    <ProductLayout>
      <div className="min-h-screen bg-background py-8">
        <div className="container mx-auto px-4 md:px-6">
          <div className="mb-8">
            <Button
              variant="ghost"
              onClick={() => navigate("/calculators")}
              className="mb-4 pl-0 hover:bg-transparent hover:text-primary"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Calculators
            </Button>
            <div className="flex items-center gap-4">
              <div className="rounded-lg bg-primary/10 p-3 text-primary">
                <Calculator className="h-8 w-8" />
              </div>
              <h1 className="text-3xl font-bold text-secondary">
                RD Calculator
              </h1>
            </div>
          </div>

          <div className="mx-auto max-w-7xl">
            <div className="grid gap-10 lg:grid-cols-2">
              <Card className="border-border bg-card">
                <CardContent className="space-y-6 p-6 md:p-8">
                  <div className="mb-2 flex items-center gap-3">
                    <div className="rounded-xl bg-purple-100 p-3">
                      <TrendingUp className="h-6 w-6 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-secondary">
                        RD Calculator
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Recurring Deposit Planning
                      </p>
                    </div>
                  </div>
                  <div className="space-y-6">
                    <CompactCalculatorRow
                      label="Monthly Deposit"
                      value={rdAmount}
                      onChange={setRdAmount}
                      min={500}
                      max={100000}
                      step={500}
                      prefix="₹"
                      colorClass="text-primary"
                    />

                    <CompactCalculatorRow
                      label="Interest Rate"
                      value={rdRate}
                      onChange={setRdRate}
                      min={3}
                      max={15}
                      step={0.1}
                      suffix="%"
                      colorClass="text-purple-600"
                    />

                    <CompactCalculatorRow
                      label="Time Period"
                      value={rdYears}
                      onChange={setRdYears}
                      min={1}
                      max={10}
                      step={1}
                      suffix="Yrs"
                      colorClass="text-blue-600"
                    />
                  </div>
                </CardContent>
              </Card>
              <Card className="border-border bg-muted/20">
                <CardContent className="p-8">
                  <div className="mb-8 text-center">
                    <p className="mb-1 text-xs font-bold uppercase tracking-wider text-muted-foreground">
                      Maturity Value
                    </p>
                    <h2 className="text-4xl font-black text-purple-700">
                      {formatCurrency(rdResult.maturity)}
                    </h2>
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between border-b border-slate-100 py-4">
                      <span className="font-medium text-muted-foreground">
                        Total Deposited
                      </span>
                      <span className="text-xl font-bold text-secondary">
                        {formatCurrency(rdResult.invested)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between border-b border-slate-100 py-4">
                      <span className="font-medium text-muted-foreground">
                        Interest Earned
                      </span>
                      <span className="text-xl font-bold text-green-600">
                        {formatCurrency(rdResult.interest)}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </ProductLayout>
  );
};
